// Season confirmations on the Customers page (migration 094).
//
// season_status and season_key arrive on each customer row from
// GET /api/customers: pending, confirmed or declined for the season the
// confirmations are about, or null when the customer has no row (the season
// has not been started, or they were added since). A property carries its
// parent's. The answer is recorded with POST /api/customers/:id/season-status.

export const SEASON_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'declined', label: 'Declined' },
  { value: 'pending', label: 'No reply' },
];

const STATUS_WORDS = { confirmed: 'Confirmed', declined: 'Declined', pending: 'No reply' };

// { label: 'Confirmed 2026-27', className }, or null with no row.
export function seasonBadge(row) {
  if (!row || !STATUS_WORDS[row.season_status]) return null;
  const label = row.season_key ? `${STATUS_WORDS[row.season_status]} ${row.season_key}` : STATUS_WORDS[row.season_status];
  return { label, className: `season-badge season-${row.season_status}` };
}

export function matchesSeasonFilter(row, filter) {
  if (!filter || filter === 'all') return true;
  return Boolean(row) && row.season_status === filter;
}

// The filter as the printed list names it, or null for All.
export function seasonFilterPrintLabel(filter, seasonKey) {
  const f = SEASON_FILTERS.find((x) => x.value === filter);
  if (!f || f.value === 'all') return null;
  return seasonKey ? `${f.label} ${seasonKey} only` : `${f.label} only`;
}

// The Confirmed chooser's starting values, from what the card holds now.
export function chooserDefaults(row) {
  return {
    servicePreference: row && row.service_preference === 'seasonal' ? 'seasonal' : 'on_call',
    wantsSalt: Boolean(row && (row.salt_preference === 'always' || row.salt_preference === 'as_needed')),
    snowTierId: row && row.snow_tier_id ? String(row.snow_tier_id) : '',
  };
}

// The POST body for a confirmation from the chooser. An empty size sends none,
// so the customer's tier is left as it is.
export function confirmBody(choice) {
  return {
    status: 'confirmed',
    servicePreference: choice.servicePreference,
    wantsSalt: choice.wantsSalt === true,
    snowTierId: choice.snowTierId ? Number(choice.snowTierId) : null,
  };
}

// The route builder's one-line warning after a customer is added by hand who
// declined the season or has not replied. null for anyone else. Adding them
// is allowed; this only says so.
export function seasonAddWarning(option) {
  if (!option) return null;
  const season = option.seasonKey ? ` ${option.seasonKey}` : '';
  if (option.seasonStatus === 'declined') return `${option.displayName} declined the${season} season. Added anyway.`;
  if (option.seasonStatus === 'pending') return `${option.displayName} has not replied for the${season} season. Added anyway.`;
  return null;
}

// The route builder's Available list: customers who declined the season are
// left out unless Show declined is ticked. declinedCount is how many that
// hides, so the checkbox can say so. Add all skips them either way.
export function availableStopOptions(options, showDeclined) {
  const list = options || [];
  const declinedCount = list.filter((o) => o.seasonStatus === 'declined').length;
  return {
    visible: showDeclined ? list : list.filter((o) => o.seasonStatus !== 'declined'),
    declinedCount,
  };
}

// What Add all says it did, from POST /api/routes/:id/stops/add-all.
export function addAllSummary(r) {
  const parts = [`Added ${r.added} ${r.added === 1 ? 'stop' : 'stops'}`];
  if (r.skippedDeclined > 0) parts.push(`skipped ${r.skippedDeclined} who declined the ${r.seasonKey} season`);
  if (r.skippedDoNotServe > 0) parts.push(`skipped ${r.skippedDoNotServe} marked Do Not Serve`);
  if (r.skippedNeedsTier > 0) parts.push(`skipped ${r.skippedNeedsTier} with no size tier (depth route)`);
  if (r.skippedNoRequest > 0) parts.push(`skipped ${r.skippedNoRequest} on-call with no request for this day`);
  return `${parts.join('; ')}.`;
}

// What the toast says after a save.
export function seasonSavedMessage(status, { sizeFlagged } = {}) {
  if (status === 'confirmed') {
    return sizeFlagged ? 'Confirmed. The size changed, so it is marked Size unconfirmed for you to check.' : 'Confirmed for the season';
  }
  if (status === 'declined') return 'Marked declined for the season';
  return 'Back to no reply';
}
